import { create } from 'zustand';
import { ConsultantNode, PayoutBreakdown } from '../types/career';
import {
  AutoPathwayConfig,
  MonthlySimulationStep,
  PresetStructureId,
  SimulationMode,
  SimulationResult,
} from '../types/simulation';
import { DEFAULT_AUTO_PATHWAY_CONFIG, runAutoPathwaySimulation } from '../simulation/autoPathway';
import {
  addNode,
  deleteNode,
  recalculateSandboxTree,
  toggleNodeActive,
  updateNode,
} from '../simulation/sandboxEngine';
import { getPresetTree } from '../simulation/presets';
import { cloneTree } from '../utils/treeUtils';
import { DEFAULT_USD_EXCHANGE_RATE, calculateNodePayout } from '../math/payout';

export interface SimulationStoreState {
  mode: SimulationMode;
  selectedMonth: number;
  autoPathwayConfig: AutoPathwayConfig;
  simulationResult: SimulationResult;
  sandboxTree: ConsultantNode;
  activePreset: PresetStructureId | null;
  pathwayInitialTree: ConsultantNode | null;
  usdExchangeRate: number;
  commissionRateCoefficient: number;
  activeStructureTitle: string | null;
  simulationStartDate: string;

  // Actions
  setMode: (mode: SimulationMode) => void;
  setSelectedMonth: (month: number) => void;
  setCommissionRateCoefficient: (coeff: number) => void;
  setActiveStructureTitle: (title: string | null) => void;
  setSimulationStartDate: (dateStr: string) => void;
  updateAutoPathwayConfig: (config: Partial<AutoPathwayConfig>) => void;
  runAutoSimulation: () => void;
  loadPreset: (presetId: PresetStructureId) => void;
  loadCustomTree: (tree: ConsultantNode, title?: string) => void;
  transferPathwayToSandbox: () => void;
  transferSandboxToPathway: () => void;
  resetPathwayToNewbie: () => void;
  addSandboxNode: (parentId: string, nodeData?: Partial<ConsultantNode>) => ConsultantNode;
  updateSandboxNode: (id: string, data: Partial<ConsultantNode>) => void;
  deleteSandboxNode: (id: string) => void;
  toggleSandboxNodeActive: (id: string) => void;
  setUsdExchangeRate: (rate: number) => void;

  // Getters / Selectors
  getCurrentRootNode: () => ConsultantNode;
  getCurrentPayoutBreakdown: () => PayoutBreakdown;
  getCurrentStep: () => MonthlySimulationStep | null;
}

const initialConfig: AutoPathwayConfig = {
  ...DEFAULT_AUTO_PATHWAY_CONFIG,
  months: 12,
  newbieMeetingsPerMonth: 20,
  leaderMeetingsPerMonth: 10,
  conversionRate: 0.10,
  meetingsPerContract: 10,
  contractSizeAE: 50.0,
  averageContractAE: 50.0,
  recruitmentPace: 1.0,
  teamSeminarRecruitPercent: 20,
  attritionRate: 0.30,
  preOnboardingAttritionRate: 0.30,
  leaderPersonalMeetings: 20,
  leaderRecruitmentPace: 1.0,
  leaderCeaseLevel: 4,
  teamCeaseLevel: 0,
};

const getInitialSimulationStartDate = (): string => {
  try {
    const saved = localStorage.getItem('starlife_simulation_start_date');
    if (saved && /^\d{4}-\d{2}$/.test(saved)) {
      return saved;
    }
  } catch {
    // ignore
  }
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const initialAutoResult = runAutoPathwaySimulation(initialConfig);
const initialSandboxTree = getPresetTree('level_4_team');

export const useSimulationStore = create<SimulationStoreState>((set, get) => ({
  mode: 'pathway',
  selectedMonth: 12,
  autoPathwayConfig: initialConfig,
  simulationResult: initialAutoResult,
  sandboxTree: initialSandboxTree,
  activePreset: 'level_4_team',
  pathwayInitialTree: null,
  activeStructureTitle: null,
  usdExchangeRate: DEFAULT_USD_EXCHANGE_RATE,
  commissionRateCoefficient: 1.0,
  simulationStartDate: getInitialSimulationStartDate(),

  setMode: (mode: SimulationMode) => set({ mode }),
  setCommissionRateCoefficient: (coeff: number) => set({ commissionRateCoefficient: coeff }),
  setActiveStructureTitle: (title: string | null) => set({ activeStructureTitle: title }),
  setSimulationStartDate: (dateStr: string) => {
    try {
      localStorage.setItem('starlife_simulation_start_date', dateStr);
    } catch {
      // ignore
    }
    set({ simulationStartDate: dateStr });
  },

  setSelectedMonth: (month: number) => {
    const totalMonths = get().simulationResult?.totalMonths || 12;
    const clampedMonth = Math.max(1, Math.min(totalMonths, month));
    set({ selectedMonth: clampedMonth });
  },

  updateAutoPathwayConfig: (configUpdate: Partial<AutoPathwayConfig>) => {
    const newConfig = { ...get().autoPathwayConfig, ...configUpdate };
    const initialTree = get().pathwayInitialTree;
    const newResult = runAutoPathwaySimulation(newConfig, initialTree || undefined);
    const clampedMonth = Math.min(get().selectedMonth, newResult.totalMonths);
    set({
      autoPathwayConfig: newConfig,
      simulationResult: newResult,
      selectedMonth: clampedMonth,
    });
  },

  runAutoSimulation: () => {
    const currentConfig = get().autoPathwayConfig;
    const initialTree = get().pathwayInitialTree;
    const newResult = runAutoPathwaySimulation(currentConfig, initialTree || undefined);
    const clampedMonth = Math.min(get().selectedMonth, newResult.totalMonths);
    set({
      simulationResult: newResult,
      selectedMonth: clampedMonth,
    });
  },

  loadPreset: (presetId: PresetStructureId) => {
    const tree = getPresetTree(presetId);
    set({
      sandboxTree: tree,
      activePreset: presetId,
      activeStructureTitle: null,
    });
  },

  loadCustomTree: (tree: ConsultantNode, title?: string) => {
    set({
      sandboxTree: cloneTree(tree),
      activePreset: null,
      activeStructureTitle: title || tree.name,
      mode: 'sandbox',
    });
  },

  transferPathwayToSandbox: () => {
    const currentRoot = get().getCurrentRootNode();
    const cloned = cloneTree(currentRoot);
    set({
      sandboxTree: cloned,
      activePreset: null,
      activeStructureTitle: null,
      mode: 'sandbox',
    });
  },

  transferSandboxToPathway: () => {
    const currentTree = get().sandboxTree;
    const cloned = cloneTree(currentTree);
    const config = get().autoPathwayConfig;
    const newResult = runAutoPathwaySimulation(config, cloned);
    set({
      pathwayInitialTree: cloned,
      simulationResult: newResult,
      selectedMonth: 1,
      mode: 'pathway',
    });
  },

  resetPathwayToNewbie: () => {
    const config = get().autoPathwayConfig;
    const newResult = runAutoPathwaySimulation(config);
    set({
      pathwayInitialTree: null,
      simulationResult: newResult,
      selectedMonth: 1,
    });
  },

  addSandboxNode: (parentId: string, nodeData?: Partial<ConsultantNode>): ConsultantNode => {
    const currentTree = get().sandboxTree;
    const { root, newNode } = addNode(currentTree, parentId, nodeData);
    set({
      mode: 'sandbox',
      sandboxTree: root,
      activePreset: null, // custom modification
    });
    return newNode;
  },

  updateSandboxNode: (id: string, data: Partial<ConsultantNode>) => {
    const currentTree = get().sandboxTree;
    const updatedTree = updateNode(currentTree, id, data);
    set({
      sandboxTree: updatedTree,
      activePreset: null,
    });
  },

  deleteSandboxNode: (id: string) => {
    const currentTree = get().sandboxTree;
    const updatedTree = deleteNode(currentTree, id);
    set({
      sandboxTree: updatedTree,
      activePreset: null,
    });
  },

  toggleSandboxNodeActive: (id: string) => {
    const currentTree = get().sandboxTree;
    const updatedTree = toggleNodeActive(currentTree, id);
    set({
      sandboxTree: updatedTree,
      activePreset: null,
    });
  },

  setUsdExchangeRate: (rate: number) => {
    set({ usdExchangeRate: rate });
    get().runAutoSimulation();
  },

  getCurrentRootNode: () => {
    const state = get();
    if (state.mode === 'pathway') {
      const step = state.getCurrentStep();
      return step ? step.rootConsultant : state.simulationResult.steps[0].rootConsultant;
    }
    return state.sandboxTree;
  },

  getCurrentPayoutBreakdown: () => {
    const state = get();
    if (state.mode === 'pathway') {
      const step = state.getCurrentStep();
      const root = step ? step.rootConsultant : state.simulationResult.steps[0].rootConsultant;
      return calculateNodePayout(root, state.usdExchangeRate, state.commissionRateCoefficient);
    }
    const { payout } = recalculateSandboxTree(state.sandboxTree, state.usdExchangeRate, state.commissionRateCoefficient);
    return payout;
  },

  getCurrentStep: () => {
    const state = get();
    if (state.mode !== 'pathway' || !state.simulationResult) {
      return null;
    }
    const stepIndex = Math.max(0, Math.min(state.selectedMonth - 1, state.simulationResult.steps.length - 1));
    return state.simulationResult.steps[stepIndex] || null;
  },
}));
